// Pane boundaries the reader drags (Resizer.svelte): sizes are clamped to
// bounds, moved by keys, and stored as whole units (pixels or percent).

export interface Bounds {
  min: number;
  max: number;
}

/** `v` rounded and kept within `b`; the minimum wins when the bounds cross. */
export function clamp(v: number, b: Bounds): number {
  return Math.max(b.min, Math.min(b.max, Math.round(v)));
}

/** A stored size, clamped to today's bounds; anything but a finite number is unset (null), so the default applies. */
export function storedSize(raw: unknown, b: Bounds): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) ? clamp(raw, b) : null;
}

export type KeyResize = { kind: 'set'; value: number } | { kind: 'reset' } | null;

/**
 * What a key does to a vertical boundary at `value`: Left and Right move it
 * by `step` (Shift: five steps), Home and End to an end, Enter resets it to
 * its default. Null for any other key, and for arrows with Ctrl, Alt or
 * Meta, which belong to the browser and the page.
 */
export function keyResize(
  e: { key: string; shiftKey?: boolean; ctrlKey?: boolean; altKey?: boolean; metaKey?: boolean },
  value: number,
  b: Bounds,
  step: number,
): KeyResize {
  if (e.ctrlKey || e.altKey || e.metaKey) return null;
  const by = e.shiftKey ? step * 5 : step;
  switch (e.key) {
    case 'ArrowLeft':
      return { kind: 'set', value: clamp(value - by, b) };
    case 'ArrowRight':
      return { kind: 'set', value: clamp(value + by, b) };
    case 'Home':
      return { kind: 'set', value: b.min };
    case 'End':
      return { kind: 'set', value: b.max };
    case 'Enter':
      return { kind: 'reset' };
    default:
      return null;
  }
}

/**
 * The Files view's file list, in pixels: at least `min`, at most `max`, and
 * never so wide that the diff beside it (past the `gap`) is under `minDiff`.
 */
export const EXPLORER = { min: 200, max: 640, step: 16, gap: 16, minDiff: 420 } as const;

/** The file list's width until the reader sizes it, by window width (the breakpoints of diff.css). */
export function explorerDefault(viewport: number): number {
  if (viewport >= 1800) return 340;
  if (viewport <= 1100) return 255;
  return 310;
}

/** The file list's bounds in a layout `width` pixels wide. */
export function explorerBounds(width: number): Bounds {
  return { min: EXPLORER.min, max: Math.max(EXPLORER.min, Math.min(EXPLORER.max, width - EXPLORER.gap - EXPLORER.minDiff)) };
}

/**
 * Diff rows a pane boundary moves live over. The diff is not virtualized,
 * so resizing lays out every row: about 0.04 ms a wrapped row, 185 ms for
 * 5000, measured in Chrome on an M-series Mac. Past a frame's worth of rows
 * the boundary draws a guide line instead (Resizer's `deferred`).
 */
export const LIVE_ROWS = 400;

/** Whether a boundary beside `rows` diff rows moves live. */
export function liveResize(rows: number): boolean {
  return rows <= LIVE_ROWS;
}

/** The split diff's base column, in percent of the diff's width. */
export const SPLIT = { min: 20, max: 80, step: 2, default: 50 } as const;

/**
 * The Overview's side columns, in pixels (the --ov-left, --ov-right and
 * --ov-side of views.css): the outline and Links, and the merge and review
 * cards in three columns; the merge and review cards in two.
 */
export const OVERVIEW = {
  left: { min: 220, max: 440, default: 280 },
  right: { min: 260, max: 480, default: 320 },
  side: { min: 260, max: 440, default: 300 },
  step: 16,
  /** The description and conversation keep at least this width beside the side columns. */
  minMain: 560,
  /** The gap between columns: views.css's --ov-gap3 in three columns, --ov-gap in two. */
  gap3: 24,
  gap: 20,
} as const;

/**
 * Side column `k`'s bounds on an Overview `width` pixels wide, beside the
 * other side column of `other` pixels (three columns; ignored for `side`),
 * so the middle column keeps at least OVERVIEW.minMain.
 */
export function overviewBounds(k: 'left' | 'right' | 'side', width: number, other: number): Bounds {
  const room = k === 'side' ? width - OVERVIEW.gap - OVERVIEW.minMain : width - 2 * OVERVIEW.gap3 - other - OVERVIEW.minMain;
  return { min: OVERVIEW[k].min, max: Math.max(OVERVIEW[k].min, Math.min(OVERVIEW[k].max, room)) };
}

/**
 * Pixels of column width per pixel of pointer travel on the Overview, `width`
 * wide with columns of `columns` pixels in all. While the columns are
 * narrower than the page they are centred: widening one by two pixels moves
 * its inner edge by one. Once they fill the page, the middle column gives
 * way and the edge follows the pointer.
 */
export function overviewPerPx(width: number, columns: number): number {
  return width > columns ? 2 : 1;
}
